"use client";

// src/components/ThemeProvider.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

// src/themes/base.ts
var baseSpacing = {
  "--pc-space-page-x": "1.5rem",
  "--pc-space-page-y": "1.5rem",
  "--pc-space-section-gap": "2rem",
  "--pc-space-card-padding": "1.5rem",
  "--pc-space-card-gap": "1rem",
  "--pc-space-input-x": "0.75rem",
  "--pc-space-input-y": "0.5rem",
  "--pc-space-btn-x": "1rem",
  "--pc-space-btn-y": "0.5rem",
  "--pc-space-btn-gap": "0.5rem"
};
var baseTypography = {
  "--pc-font-sans": 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
  "--pc-font-mono": 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  "--pc-text-xs": "0.75rem",
  "--pc-text-sm": "0.875rem",
  "--pc-text-base": "1rem",
  "--pc-text-lg": "1.125rem",
  "--pc-text-xl": "1.25rem",
  "--pc-text-2xl": "1.5rem",
  "--pc-text-3xl": "1.875rem",
  "--pc-text-h1": "2.25rem",
  "--pc-text-h2": "1.875rem",
  "--pc-text-h3": "1.5rem",
  "--pc-text-h4": "1.25rem",
  "--pc-weight-normal": "400",
  "--pc-weight-medium": "500",
  "--pc-weight-semibold": "600",
  "--pc-weight-bold": "700",
  "--pc-weight-h1": "700",
  "--pc-weight-h2": "600",
  "--pc-weight-h3": "600",
  "--pc-weight-h4": "600",
  "--pc-lh-xs": "1rem",
  "--pc-lh-sm": "1.25rem",
  "--pc-lh-base": "1.5rem",
  "--pc-lh-lg": "1.75rem",
  "--pc-lh-h1": "2.5rem",
  "--pc-lh-h2": "2.25rem",
  "--pc-lh-h3": "2rem",
  "--pc-lh-h4": "1.75rem"
};
var baseShadows = {
  "--pc-shadow-card": "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  "--pc-shadow-dropdown": "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  "--pc-shadow-modal": "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
  "--pc-shadow-popover": "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  "--pc-shadow-toast": "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)"
};
var baseRadius = {
  "--pc-radius-btn": "0.375rem",
  "--pc-radius-input": "0.375rem",
  "--pc-radius-card": "0.5rem",
  "--pc-radius-modal": "0.75rem",
  "--pc-radius-badge": "9999px",
  "--pc-radius-full": "9999px"
};
var baseTransitions = {
  "--pc-transition-fast": "150ms ease",
  "--pc-transition-normal": "200ms ease",
  "--pc-transition-slow": "300ms ease"
};

// src/themes/meridian.ts
var lightColors = {
  // Background - Warm Stone
  "--pc-bg-base": "#FFFCF9",
  "--pc-bg-subtle": "#FAF7F5",
  "--pc-bg-muted": "#F1ECE8",
  "--pc-bg-emphasized": "#E7E0DA",
  "--pc-bg-inverted": "#292524",
  // Foreground - Warm Grays
  "--pc-fg-base": "#1C1917",
  "--pc-fg-muted": "#57534E",
  "--pc-fg-subtle": "#A8A29E",
  "--pc-fg-inverted": "#FAF7F5",
  // Primary - Terracotta Rose
  "--pc-primary": "#C2410C",
  "--pc-primary-hover": "#9A3412",
  "--pc-primary-active": "#7C2D12",
  "--pc-primary-subtle": "#FFF7ED",
  "--pc-primary-muted": "#FFEDD5",
  "--pc-primary-fg": "#FFFFFF",
  // Secondary - Sage
  "--pc-secondary": "#4D7C6F",
  "--pc-secondary-hover": "#3D6359",
  "--pc-secondary-active": "#2D4A43",
  "--pc-secondary-subtle": "#F0F5F3",
  "--pc-secondary-fg": "#FFFFFF",
  // Border - Soft
  "--pc-border": "#E7E5E4",
  "--pc-border-muted": "#F5F5F4",
  "--pc-border-subtle": "#FAFAF9",
  "--pc-border-emphasized": "#D6D3D1",
  "--pc-border-focus": "#C2410C",
  // Success - Forest
  "--pc-success": "#15803D",
  "--pc-success-hover": "#166534",
  "--pc-success-subtle": "#F0FDF4",
  "--pc-success-fg": "#FFFFFF",
  // Warning - Amber
  "--pc-warning": "#B45309",
  "--pc-warning-hover": "#92400E",
  "--pc-warning-subtle": "#FFFBEB",
  "--pc-warning-fg": "#FFFFFF",
  // Error - Rose
  "--pc-error": "#BE123C",
  "--pc-error-hover": "#9F1239",
  "--pc-error-subtle": "#FFF1F2",
  "--pc-error-fg": "#FFFFFF",
  // Info - Teal
  "--pc-info": "#0F766E",
  "--pc-info-hover": "#115E59",
  "--pc-info-subtle": "#F0FDFA",
  "--pc-info-fg": "#FFFFFF",
  // Input
  "--pc-input-bg": "#FFFFFF",
  "--pc-input-border": "#E7E5E4",
  "--pc-input-border-focus": "#C2410C",
  "--pc-input-border-error": "#BE123C",
  "--pc-input-placeholder": "#A8A29E",
  // Overlay
  "--pc-overlay": "rgba(41, 37, 36, 0.5)",
  "--pc-overlay-heavy": "rgba(41, 37, 36, 0.8)"
};
var darkColors = {
  // Background - Warm near-black
  "--pc-bg-base": "#1C1917",
  "--pc-bg-subtle": "#292524",
  "--pc-bg-muted": "#44403C",
  "--pc-bg-emphasized": "#57534E",
  "--pc-bg-inverted": "#FAF7F5",
  // Foreground
  "--pc-fg-base": "#FAF7F5",
  "--pc-fg-muted": "#D6D3D1",
  "--pc-fg-subtle": "#78716C",
  "--pc-fg-inverted": "#1C1917",
  // Primary - Lighter orange in dark
  "--pc-primary": "#FB923C",
  "--pc-primary-hover": "#FDBA74",
  "--pc-primary-active": "#FED7AA",
  "--pc-primary-subtle": "#431407",
  "--pc-primary-muted": "#7C2D12",
  "--pc-primary-fg": "#1C1917",
  // Secondary - Sage
  "--pc-secondary": "#6EE7B7",
  "--pc-secondary-hover": "#A7F3D0",
  "--pc-secondary-active": "#D1FAE5",
  "--pc-secondary-subtle": "#022C22",
  "--pc-secondary-fg": "#1C1917",
  // Border
  "--pc-border": "#44403C",
  "--pc-border-muted": "#292524",
  "--pc-border-subtle": "#1C1917",
  "--pc-border-emphasized": "#57534E",
  "--pc-border-focus": "#FB923C",
  // Success
  "--pc-success": "#4ADE80",
  "--pc-success-hover": "#86EFAC",
  "--pc-success-subtle": "#052E16",
  "--pc-success-fg": "#1C1917",
  // Warning
  "--pc-warning": "#FBBF24",
  "--pc-warning-hover": "#FCD34D",
  "--pc-warning-subtle": "#451A03",
  "--pc-warning-fg": "#1C1917",
  // Error
  "--pc-error": "#FB7185",
  "--pc-error-hover": "#FDA4AF",
  "--pc-error-subtle": "#4C0519",
  "--pc-error-fg": "#1C1917",
  // Info
  "--pc-info": "#2DD4BF",
  "--pc-info-hover": "#5EEAD4",
  "--pc-info-subtle": "#042F2E",
  "--pc-info-fg": "#1C1917",
  // Input
  "--pc-input-bg": "#292524",
  "--pc-input-border": "#44403C",
  "--pc-input-border-focus": "#FB923C",
  "--pc-input-border-error": "#FB7185",
  "--pc-input-placeholder": "#78716C",
  // Overlay
  "--pc-overlay": "rgba(0, 0, 0, 0.6)",
  "--pc-overlay-heavy": "rgba(0, 0, 0, 0.85)"
};
var meridianSpacing = {
  "--pc-space-page-x": "2rem",
  "--pc-space-page-y": "2rem",
  "--pc-space-section-gap": "3rem",
  "--pc-space-card-padding": "2rem",
  "--pc-space-card-gap": "1.5rem",
  "--pc-space-input-x": "1rem",
  "--pc-space-input-y": "0.75rem",
  "--pc-space-btn-x": "1.5rem",
  "--pc-space-btn-y": "0.625rem",
  "--pc-space-btn-gap": "0.5rem"
};
var meridianTypography = {
  "--pc-font-sans": '"DM Sans", ui-sans-serif, system-ui, sans-serif',
  "--pc-font-mono": '"DM Mono", ui-monospace, SFMono-Regular, monospace',
  "--pc-text-xs": "0.75rem",
  "--pc-text-sm": "0.875rem",
  "--pc-text-base": "1rem",
  "--pc-text-lg": "1.125rem",
  "--pc-text-xl": "1.25rem",
  "--pc-text-2xl": "1.5rem",
  "--pc-text-3xl": "2rem",
  "--pc-text-h1": "2.5rem",
  "--pc-text-h2": "1.75rem",
  "--pc-text-h3": "1.25rem",
  "--pc-text-h4": "1.125rem",
  "--pc-weight-normal": "400",
  "--pc-weight-medium": "500",
  "--pc-weight-semibold": "600",
  "--pc-weight-bold": "700",
  "--pc-weight-h1": "600",
  "--pc-weight-h2": "600",
  "--pc-weight-h3": "500",
  "--pc-weight-h4": "500",
  "--pc-lh-xs": "1rem",
  "--pc-lh-sm": "1.375rem",
  "--pc-lh-base": "1.625rem",
  "--pc-lh-lg": "1.75rem",
  "--pc-lh-h1": "3rem",
  "--pc-lh-h2": "2.25rem",
  "--pc-lh-h3": "1.75rem",
  "--pc-lh-h4": "1.5rem"
};
var meridianShadows = {
  "--pc-shadow-card": "0 1px 2px rgba(0, 0, 0, 0.04), 0 2px 4px rgba(0, 0, 0, 0.02)",
  "--pc-shadow-dropdown": "0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)",
  "--pc-shadow-modal": "0 24px 48px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)",
  "--pc-shadow-popover": "0 8px 24px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04)",
  "--pc-shadow-toast": "0 8px 24px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04)"
};
var meridianRadius = {
  "--pc-radius-btn": "9999px",
  "--pc-radius-input": "0.75rem",
  "--pc-radius-card": "1rem",
  "--pc-radius-modal": "1.25rem",
  "--pc-radius-badge": "9999px",
  "--pc-radius-full": "9999px"
};
var meridianTransitions = {
  "--pc-transition-fast": "200ms cubic-bezier(0.4, 0, 0.2, 1)",
  "--pc-transition-normal": "300ms cubic-bezier(0.4, 0, 0.2, 1)",
  "--pc-transition-slow": "400ms cubic-bezier(0.4, 0, 0.2, 1)"
};
var meridianTheme = {
  name: "meridian",
  displayName: "Meridian",
  colors: {
    light: lightColors,
    dark: darkColors
  },
  spacing: meridianSpacing,
  typography: meridianTypography,
  shadows: meridianShadows,
  radius: meridianRadius,
  transitions: meridianTransitions
};
var meridianFontLink = "https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&display=swap";
var meridianDisplayFont = '"Fraunces", Georgia, serif';

// src/themes/createProductTheme.ts
function createProductTheme(name, displayName, colors2) {
  const buildColors = (palette, baseColors) => ({
    // Backgrounds - use product colors or fallback to base
    "--pc-bg-base": palette.bgBase ?? baseColors["--pc-bg-base"],
    "--pc-bg-subtle": palette.bgSubtle ?? baseColors["--pc-bg-subtle"],
    "--pc-bg-muted": palette.bgMuted ?? baseColors["--pc-bg-muted"],
    "--pc-bg-emphasized": palette.bgEmphasized ?? baseColors["--pc-bg-emphasized"],
    "--pc-bg-inverted": palette.bgInverted ?? baseColors["--pc-bg-inverted"],
    // Foregrounds - use product colors or fallback to base
    "--pc-fg-base": palette.fgBase ?? baseColors["--pc-fg-base"],
    "--pc-fg-muted": palette.fgMuted ?? baseColors["--pc-fg-muted"],
    "--pc-fg-subtle": palette.fgSubtle ?? baseColors["--pc-fg-subtle"],
    "--pc-fg-inverted": palette.fgInverted ?? baseColors["--pc-fg-inverted"],
    // Primary - always from product palette
    "--pc-primary": palette.primary,
    "--pc-primary-hover": palette.primaryHover,
    "--pc-primary-active": palette.primaryActive,
    "--pc-primary-subtle": palette.primarySubtle,
    "--pc-primary-muted": palette.primaryMuted,
    "--pc-primary-fg": palette.primaryFg,
    // Secondary - product colors or fallback to base
    "--pc-secondary": palette.secondary ?? baseColors["--pc-secondary"],
    "--pc-secondary-hover": palette.secondaryHover ?? baseColors["--pc-secondary-hover"],
    "--pc-secondary-active": palette.secondaryActive ?? baseColors["--pc-secondary-active"],
    "--pc-secondary-subtle": palette.secondarySubtle ?? baseColors["--pc-secondary-subtle"],
    "--pc-secondary-fg": palette.secondaryFg ?? baseColors["--pc-secondary-fg"],
    // Borders - product colors or fallback to base, focus uses primary
    "--pc-border": palette.border ?? baseColors["--pc-border"],
    "--pc-border-muted": palette.borderMuted ?? baseColors["--pc-border-muted"],
    "--pc-border-subtle": palette.borderSubtle ?? baseColors["--pc-border-subtle"],
    "--pc-border-emphasized": palette.borderEmphasized ?? baseColors["--pc-border-emphasized"],
    "--pc-border-focus": palette.primary,
    // Focus ring uses primary color
    // Status colors - inherit from Meridian (consistent across products)
    "--pc-success": baseColors["--pc-success"],
    "--pc-success-hover": baseColors["--pc-success-hover"],
    "--pc-success-subtle": baseColors["--pc-success-subtle"],
    "--pc-success-fg": baseColors["--pc-success-fg"],
    "--pc-warning": baseColors["--pc-warning"],
    "--pc-warning-hover": baseColors["--pc-warning-hover"],
    "--pc-warning-subtle": baseColors["--pc-warning-subtle"],
    "--pc-warning-fg": baseColors["--pc-warning-fg"],
    "--pc-error": baseColors["--pc-error"],
    "--pc-error-hover": baseColors["--pc-error-hover"],
    "--pc-error-subtle": baseColors["--pc-error-subtle"],
    "--pc-error-fg": baseColors["--pc-error-fg"],
    "--pc-info": baseColors["--pc-info"],
    "--pc-info-hover": baseColors["--pc-info-hover"],
    "--pc-info-subtle": baseColors["--pc-info-subtle"],
    "--pc-info-fg": baseColors["--pc-info-fg"],
    // Input - use product border or fallback, focus uses primary
    "--pc-input-bg": palette.bgBase ?? baseColors["--pc-input-bg"],
    "--pc-input-border": palette.border ?? baseColors["--pc-input-border"],
    "--pc-input-border-focus": palette.primary,
    "--pc-input-border-error": baseColors["--pc-input-border-error"],
    "--pc-input-placeholder": palette.fgSubtle ?? baseColors["--pc-input-placeholder"],
    // Overlay - inherit from Meridian
    "--pc-overlay": baseColors["--pc-overlay"],
    "--pc-overlay-heavy": baseColors["--pc-overlay-heavy"]
  });
  return {
    name,
    displayName,
    colors: {
      light: buildColors(colors2.light, meridianTheme.colors.light),
      dark: buildColors(colors2.dark, meridianTheme.colors.dark)
    },
    // Inherit all non-color tokens from Meridian
    spacing: meridianTheme.spacing,
    typography: meridianTheme.typography,
    shadows: meridianTheme.shadows,
    radius: meridianTheme.radius,
    transitions: meridianTheme.transitions
  };
}
var productPalettes = {
  /**
   * Arc2 - Indigo/Violet professional
   * For: AI-powered analytics and insights platform
   */
  arc2: {
    light: {
      primary: "#4F46E5",
      // Indigo 600
      primaryHover: "#4338CA",
      // Indigo 700
      primaryActive: "#3730A3",
      // Indigo 800
      primarySubtle: "#EEF2FF",
      // Indigo 50
      primaryMuted: "#E0E7FF",
      // Indigo 100
      primaryFg: "#FFFFFF",
      secondary: "#7C3AED",
      // Violet 600
      secondaryHover: "#6D28D9",
      // Violet 700
      secondaryActive: "#5B21B6",
      // Violet 800
      secondarySubtle: "#F5F3FF",
      // Violet 50
      secondaryFg: "#FFFFFF"
    },
    dark: {
      primary: "#818CF8",
      // Indigo 400
      primaryHover: "#A5B4FC",
      // Indigo 300
      primaryActive: "#C7D2FE",
      // Indigo 200
      primarySubtle: "#1E1B4B",
      // Indigo 950
      primaryMuted: "#312E81",
      // Indigo 900
      primaryFg: "#1C1917",
      secondary: "#A78BFA",
      // Violet 400
      secondaryHover: "#C4B5FD",
      // Violet 300
      secondaryActive: "#DDD6FE",
      // Violet 200
      secondarySubtle: "#2E1065",
      // Violet 950
      secondaryFg: "#1C1917"
    }
  },
  /**
   * Mazo - Violet/Purple professional
   * For: AI-powered LinkedIn content platform
   * Brand color: #512FEB (from mazo.framer.ai website)
   */
  mazo: {
    light: {
      primary: "#512FEB",
      // Mazo Violet (brand primary)
      primaryHover: "#4425D0",
      // Violet darker
      primaryActive: "#3820A8",
      // Violet darkest
      primarySubtle: "#F5F3FF",
      // Violet 50
      primaryMuted: "#EDE9FE",
      // Violet 100
      primaryFg: "#FFFFFF",
      secondary: "#7C3AED",
      // Violet 600 (accent)
      secondaryHover: "#6D28D9",
      // Violet 700
      secondaryActive: "#5B21B6",
      // Violet 800
      secondarySubtle: "#F5F3FF",
      // Violet 50
      secondaryFg: "#FFFFFF"
    },
    dark: {
      primary: "#7C6BF0",
      // Violet lighter for dark mode
      primaryHover: "#9B8FF5",
      // Violet 300
      primaryActive: "#BAB3FA",
      // Violet 200
      primarySubtle: "#1A0F3D",
      // Deep violet
      primaryMuted: "#2D1F6B",
      // Violet 900
      primaryFg: "#FFFFFF",
      secondary: "#A78BFA",
      // Violet 400
      secondaryHover: "#C4B5FD",
      // Violet 300
      secondaryActive: "#DDD6FE",
      // Violet 200
      secondarySubtle: "#2E1065",
      // Violet 950
      secondaryFg: "#1C1917"
    }
  },
  /**
   * Brandchord - Cyan/Pink gradient aesthetic
   * For: Brand management and creative tools
   * Dark-mode-first design with gradient accents
   */
  brandchord: {
    light: {
      primary: "#0891B2",
      // Cyan 600 (main brand color)
      primaryHover: "#0E7490",
      // Cyan 700
      primaryActive: "#155E75",
      // Cyan 800
      primarySubtle: "#ECFEFF",
      // Cyan 50
      primaryMuted: "#CFFAFE",
      // Cyan 100
      primaryFg: "#FFFFFF",
      secondary: "#DB2777",
      // Pink 600 (accent for gradients)
      secondaryHover: "#BE185D",
      // Pink 700
      secondaryActive: "#9D174D",
      // Pink 800
      secondarySubtle: "#FDF2F8",
      // Pink 50
      secondaryFg: "#FFFFFF",
      // Warm neutral backgrounds
      bgBase: "#FFFCF9",
      bgSubtle: "#FAF7F5",
      bgMuted: "#F1ECE8"
    },
    dark: {
      primary: "#22D3EE",
      // Cyan 400 (vibrant in dark mode)
      primaryHover: "#67E8F9",
      // Cyan 300
      primaryActive: "#A5F3FC",
      // Cyan 200
      primarySubtle: "#0C1929",
      // Deep blue-black (matches screenshots)
      primaryMuted: "#164E63",
      // Cyan 900
      primaryFg: "#0F172A",
      secondary: "#F472B6",
      // Pink 400 (for gradient endpoints)
      secondaryHover: "#F9A8D4",
      // Pink 300
      secondaryActive: "#FBCFE8",
      // Pink 200
      secondarySubtle: "#500724",
      // Pink 950
      secondaryFg: "#0F172A",
      // Dark sophisticated backgrounds (from screenshots)
      bgBase: "#0F172A",
      // Slate 900 - main dark bg
      bgSubtle: "#1E293B",
      // Slate 800
      bgMuted: "#334155",
      // Slate 700
      bgEmphasized: "#475569",
      // Slate 600
      bgInverted: "#F8FAFC",
      // Light foreground for dark bg
      fgBase: "#F1F5F9",
      // Slate 100
      fgMuted: "#CBD5E1",
      // Slate 300
      fgSubtle: "#64748B",
      // Slate 500
      // Subtle borders for dark mode
      border: "#334155",
      // Slate 700
      borderMuted: "#1E293B",
      // Slate 800
      borderEmphasized: "#475569"
      // Slate 600
    }
  },
  /**
   * Meridian base (terracotta) - for reference
   */
  meridian: {
    light: {
      primary: "#C2410C",
      // Terracotta
      primaryHover: "#9A3412",
      primaryActive: "#7C2D12",
      primarySubtle: "#FFF7ED",
      primaryMuted: "#FFEDD5",
      primaryFg: "#FFFFFF"
    },
    dark: {
      primary: "#FB923C",
      primaryHover: "#FDBA74",
      primaryActive: "#FED7AA",
      primarySubtle: "#431407",
      primaryMuted: "#7C2D12",
      primaryFg: "#1C1917"
    }
  }
};

// src/themes/arc2.ts
var arc2Theme = createProductTheme(
  "arc2",
  "Arc2",
  productPalettes.arc2
);

// src/themes/brandchord.ts
var brandchordTheme = createProductTheme(
  "brandchord",
  "Brandchord",
  productPalettes.brandchord
);

// src/themes/mazo.ts
var mazoTheme = createProductTheme(
  "mazo",
  "Mazo",
  productPalettes.mazo
);

// src/themes/memoria.ts
var lightColors2 = {
  // Background
  "--pc-bg-base": "#fffbeb",
  "--pc-bg-subtle": "#fef3c7",
  "--pc-bg-muted": "#fde68a",
  "--pc-bg-emphasized": "#fcd34d",
  "--pc-bg-inverted": "#451a03",
  // Foreground
  "--pc-fg-base": "#451a03",
  "--pc-fg-muted": "#78350f",
  "--pc-fg-subtle": "#b45309",
  "--pc-fg-inverted": "#fffbeb",
  // Primary - Amber
  "--pc-primary": "#d97706",
  "--pc-primary-hover": "#b45309",
  "--pc-primary-active": "#92400e",
  "--pc-primary-subtle": "#fffbeb",
  "--pc-primary-muted": "#fde68a",
  "--pc-primary-fg": "#ffffff",
  // Secondary - Orange
  "--pc-secondary": "#ea580c",
  "--pc-secondary-hover": "#c2410c",
  "--pc-secondary-active": "#9a3412",
  "--pc-secondary-subtle": "#fff7ed",
  "--pc-secondary-fg": "#ffffff",
  // Border
  "--pc-border": "#fde68a",
  "--pc-border-muted": "#fef3c7",
  "--pc-border-subtle": "#fffbeb",
  "--pc-border-emphasized": "#fcd34d",
  "--pc-border-focus": "#fbbf24",
  // Success
  "--pc-success": "#16a34a",
  "--pc-success-hover": "#15803d",
  "--pc-success-subtle": "#f0fdf4",
  "--pc-success-fg": "#ffffff",
  // Warning
  "--pc-warning": "#d97706",
  "--pc-warning-hover": "#b45309",
  "--pc-warning-subtle": "#fffbeb",
  "--pc-warning-fg": "#ffffff",
  // Error
  "--pc-error": "#dc2626",
  "--pc-error-hover": "#b91c1c",
  "--pc-error-subtle": "#fef2f2",
  "--pc-error-fg": "#ffffff",
  // Info
  "--pc-info": "#0284c7",
  "--pc-info-hover": "#0369a1",
  "--pc-info-subtle": "#f0f9ff",
  "--pc-info-fg": "#ffffff",
  // Input
  "--pc-input-bg": "#ffffff",
  "--pc-input-border": "#fde68a",
  "--pc-input-border-focus": "#fbbf24",
  "--pc-input-border-error": "#f87171",
  "--pc-input-placeholder": "#b45309",
  // Overlay
  "--pc-overlay": "rgba(69, 26, 3, 0.5)",
  "--pc-overlay-heavy": "rgba(69, 26, 3, 0.8)"
};
var darkColors2 = {
  // Background
  "--pc-bg-base": "#1c1917",
  "--pc-bg-subtle": "#292524",
  "--pc-bg-muted": "#44403c",
  "--pc-bg-emphasized": "#57534e",
  "--pc-bg-inverted": "#fffbeb",
  // Foreground
  "--pc-fg-base": "#fef3c7",
  "--pc-fg-muted": "#fde68a",
  "--pc-fg-subtle": "#78716c",
  "--pc-fg-inverted": "#1c1917",
  // Primary - Amber
  "--pc-primary": "#fbbf24",
  "--pc-primary-hover": "#fcd34d",
  "--pc-primary-active": "#fde68a",
  "--pc-primary-subtle": "#451a03",
  "--pc-primary-muted": "#78350f",
  "--pc-primary-fg": "#1c1917",
  // Secondary - Orange
  "--pc-secondary": "#fb923c",
  "--pc-secondary-hover": "#fdba74",
  "--pc-secondary-active": "#fed7aa",
  "--pc-secondary-subtle": "#431407",
  "--pc-secondary-fg": "#1c1917",
  // Border
  "--pc-border": "#44403c",
  "--pc-border-muted": "#292524",
  "--pc-border-subtle": "#1c1917",
  "--pc-border-emphasized": "#57534e",
  "--pc-border-focus": "#fbbf24",
  // Success
  "--pc-success": "#4ade80",
  "--pc-success-hover": "#86efac",
  "--pc-success-subtle": "#052e16",
  "--pc-success-fg": "#1c1917",
  // Warning
  "--pc-warning": "#fbbf24",
  "--pc-warning-hover": "#fcd34d",
  "--pc-warning-subtle": "#451a03",
  "--pc-warning-fg": "#1c1917",
  // Error
  "--pc-error": "#f87171",
  "--pc-error-hover": "#fca5a5",
  "--pc-error-subtle": "#450a0a",
  "--pc-error-fg": "#1c1917",
  // Info
  "--pc-info": "#38bdf8",
  "--pc-info-hover": "#7dd3fc",
  "--pc-info-subtle": "#082f49",
  "--pc-info-fg": "#1c1917",
  // Input
  "--pc-input-bg": "#292524",
  "--pc-input-border": "#44403c",
  "--pc-input-border-focus": "#fbbf24",
  "--pc-input-border-error": "#f87171",
  "--pc-input-placeholder": "#78716c",
  // Overlay
  "--pc-overlay": "rgba(0, 0, 0, 0.6)",
  "--pc-overlay-heavy": "rgba(0, 0, 0, 0.85)"
};
var memoriaTheme = {
  name: "memoria",
  displayName: "Memoria",
  colors: {
    light: lightColors2,
    dark: darkColors2
  },
  spacing: baseSpacing,
  typography: baseTypography,
  shadows: baseShadows,
  radius: baseRadius,
  transitions: baseTransitions
};

// src/themes/carbon.ts
var lightColors3 = {
  // Background - Off-white paper
  "--pc-bg-base": "#FAFAFA",
  "--pc-bg-subtle": "#F4F4F5",
  "--pc-bg-muted": "#E4E4E7",
  "--pc-bg-emphasized": "#D4D4D8",
  "--pc-bg-inverted": "#18181B",
  // Foreground - High contrast
  "--pc-fg-base": "#09090B",
  "--pc-fg-muted": "#52525B",
  "--pc-fg-subtle": "#A1A1AA",
  "--pc-fg-inverted": "#FAFAFA",
  // Primary - Electric Cyan
  "--pc-primary": "#06B6D4",
  "--pc-primary-hover": "#0891B2",
  "--pc-primary-active": "#0E7490",
  "--pc-primary-subtle": "#ECFEFF",
  "--pc-primary-muted": "#A5F3FC",
  "--pc-primary-fg": "#FFFFFF",
  // Secondary - Steel
  "--pc-secondary": "#71717A",
  "--pc-secondary-hover": "#52525B",
  "--pc-secondary-active": "#3F3F46",
  "--pc-secondary-subtle": "#F4F4F5",
  "--pc-secondary-fg": "#FFFFFF",
  // Border - Sharp definition
  "--pc-border": "#E4E4E7",
  "--pc-border-muted": "#F4F4F5",
  "--pc-border-subtle": "#FAFAFA",
  "--pc-border-emphasized": "#A1A1AA",
  "--pc-border-focus": "#06B6D4",
  // Success
  "--pc-success": "#10B981",
  "--pc-success-hover": "#059669",
  "--pc-success-subtle": "#ECFDF5",
  "--pc-success-fg": "#FFFFFF",
  // Warning
  "--pc-warning": "#F59E0B",
  "--pc-warning-hover": "#D97706",
  "--pc-warning-subtle": "#FFFBEB",
  "--pc-warning-fg": "#18181B",
  // Error
  "--pc-error": "#EF4444",
  "--pc-error-hover": "#DC2626",
  "--pc-error-subtle": "#FEF2F2",
  "--pc-error-fg": "#FFFFFF",
  // Info
  "--pc-info": "#06B6D4",
  "--pc-info-hover": "#0891B2",
  "--pc-info-subtle": "#ECFEFF",
  "--pc-info-fg": "#FFFFFF",
  // Input
  "--pc-input-bg": "#FFFFFF",
  "--pc-input-border": "#D4D4D8",
  "--pc-input-border-focus": "#06B6D4",
  "--pc-input-border-error": "#EF4444",
  "--pc-input-placeholder": "#A1A1AA",
  // Overlay
  "--pc-overlay": "rgba(24, 24, 27, 0.5)",
  "--pc-overlay-heavy": "rgba(24, 24, 27, 0.8)"
};
var darkColors3 = {
  // Background - Near black
  "--pc-bg-base": "#09090B",
  "--pc-bg-subtle": "#18181B",
  "--pc-bg-muted": "#27272A",
  "--pc-bg-emphasized": "#3F3F46",
  "--pc-bg-inverted": "#FAFAFA",
  // Foreground
  "--pc-fg-base": "#FAFAFA",
  "--pc-fg-muted": "#A1A1AA",
  "--pc-fg-subtle": "#71717A",
  "--pc-fg-inverted": "#09090B",
  // Primary - Brighter cyan in dark
  "--pc-primary": "#22D3EE",
  "--pc-primary-hover": "#67E8F9",
  "--pc-primary-active": "#A5F3FC",
  "--pc-primary-subtle": "#083344",
  "--pc-primary-muted": "#155E75",
  "--pc-primary-fg": "#09090B",
  // Secondary
  "--pc-secondary": "#A1A1AA",
  "--pc-secondary-hover": "#D4D4D8",
  "--pc-secondary-active": "#E4E4E7",
  "--pc-secondary-subtle": "#27272A",
  "--pc-secondary-fg": "#09090B",
  // Border
  "--pc-border": "#3F3F46",
  "--pc-border-muted": "#27272A",
  "--pc-border-subtle": "#18181B",
  "--pc-border-emphasized": "#52525B",
  "--pc-border-focus": "#22D3EE",
  // Success
  "--pc-success": "#34D399",
  "--pc-success-hover": "#6EE7B7",
  "--pc-success-subtle": "#022C22",
  "--pc-success-fg": "#09090B",
  // Warning
  "--pc-warning": "#FBBF24",
  "--pc-warning-hover": "#FCD34D",
  "--pc-warning-subtle": "#451A03",
  "--pc-warning-fg": "#09090B",
  // Error
  "--pc-error": "#F87171",
  "--pc-error-hover": "#FCA5A5",
  "--pc-error-subtle": "#450A0A",
  "--pc-error-fg": "#09090B",
  // Info
  "--pc-info": "#22D3EE",
  "--pc-info-hover": "#67E8F9",
  "--pc-info-subtle": "#083344",
  "--pc-info-fg": "#09090B",
  // Input
  "--pc-input-bg": "#18181B",
  "--pc-input-border": "#3F3F46",
  "--pc-input-border-focus": "#22D3EE",
  "--pc-input-border-error": "#F87171",
  "--pc-input-placeholder": "#71717A",
  // Overlay
  "--pc-overlay": "rgba(0, 0, 0, 0.6)",
  "--pc-overlay-heavy": "rgba(0, 0, 0, 0.85)"
};
var carbonSpacing = {
  "--pc-space-page-x": "1rem",
  "--pc-space-page-y": "1rem",
  "--pc-space-section-gap": "1.5rem",
  "--pc-space-card-padding": "1rem",
  "--pc-space-card-gap": "0.75rem",
  "--pc-space-input-x": "0.5rem",
  "--pc-space-input-y": "0.375rem",
  "--pc-space-btn-x": "0.75rem",
  "--pc-space-btn-y": "0.375rem",
  "--pc-space-btn-gap": "0.375rem"
};
var carbonTypography = {
  "--pc-font-sans": '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif',
  "--pc-font-mono": '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
  "--pc-text-xs": "0.6875rem",
  "--pc-text-sm": "0.8125rem",
  "--pc-text-base": "0.875rem",
  "--pc-text-lg": "1rem",
  "--pc-text-xl": "1.125rem",
  "--pc-text-2xl": "1.375rem",
  "--pc-text-3xl": "1.75rem",
  "--pc-text-h1": "2rem",
  "--pc-text-h2": "1.5rem",
  "--pc-text-h3": "1.125rem",
  "--pc-text-h4": "1rem",
  "--pc-weight-normal": "400",
  "--pc-weight-medium": "500",
  "--pc-weight-semibold": "600",
  "--pc-weight-bold": "700",
  "--pc-weight-h1": "700",
  "--pc-weight-h2": "600",
  "--pc-weight-h3": "600",
  "--pc-weight-h4": "500",
  "--pc-lh-xs": "1rem",
  "--pc-lh-sm": "1.25rem",
  "--pc-lh-base": "1.5rem",
  "--pc-lh-lg": "1.625rem",
  "--pc-lh-h1": "2.25rem",
  "--pc-lh-h2": "1.875rem",
  "--pc-lh-h3": "1.5rem",
  "--pc-lh-h4": "1.375rem"
};
var carbonShadows = {
  "--pc-shadow-card": "none",
  "--pc-shadow-dropdown": "0 2px 4px rgba(0, 0, 0, 0.1)",
  "--pc-shadow-modal": "0 4px 12px rgba(0, 0, 0, 0.15)",
  "--pc-shadow-popover": "0 2px 8px rgba(0, 0, 0, 0.1)",
  "--pc-shadow-toast": "0 2px 8px rgba(0, 0, 0, 0.1)"
};
var carbonRadius = {
  "--pc-radius-btn": "2px",
  "--pc-radius-input": "2px",
  "--pc-radius-card": "2px",
  "--pc-radius-modal": "4px",
  "--pc-radius-badge": "2px",
  "--pc-radius-full": "9999px"
};
var carbonTransitions = {
  "--pc-transition-fast": "100ms linear",
  "--pc-transition-normal": "150ms linear",
  "--pc-transition-slow": "200ms linear"
};
var carbonTheme = {
  name: "carbon",
  displayName: "Carbon",
  colors: {
    light: lightColors3,
    dark: darkColors3
  },
  spacing: carbonSpacing,
  typography: carbonTypography,
  shadows: carbonShadows,
  radius: carbonRadius,
  transitions: carbonTransitions
};
var carbonFontLink = "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600;700&display=swap";

// src/themes/signal.ts
var lightColors4 = {
  // Background - Clean white
  "--pc-bg-base": "#FFFFFF",
  "--pc-bg-subtle": "#FAFAFA",
  "--pc-bg-muted": "#F4F4F5",
  "--pc-bg-emphasized": "#E4E4E7",
  "--pc-bg-inverted": "#18181B",
  // Foreground
  "--pc-fg-base": "#18181B",
  "--pc-fg-muted": "#52525B",
  "--pc-fg-subtle": "#A1A1AA",
  "--pc-fg-inverted": "#FAFAFA",
  // Primary - Electric Violet
  "--pc-primary": "#7C3AED",
  "--pc-primary-hover": "#6D28D9",
  "--pc-primary-active": "#5B21B6",
  "--pc-primary-subtle": "#F5F3FF",
  "--pc-primary-muted": "#DDD6FE",
  "--pc-primary-fg": "#FFFFFF",
  // Secondary - Cool Gray
  "--pc-secondary": "#52525B",
  "--pc-secondary-hover": "#3F3F46",
  "--pc-secondary-active": "#27272A",
  "--pc-secondary-subtle": "#F4F4F5",
  "--pc-secondary-fg": "#FFFFFF",
  // Border
  "--pc-border": "#E4E4E7",
  "--pc-border-muted": "#F4F4F5",
  "--pc-border-subtle": "#FAFAFA",
  "--pc-border-emphasized": "#D4D4D8",
  "--pc-border-focus": "#7C3AED",
  // Success - Neon Green
  "--pc-success": "#22C55E",
  "--pc-success-hover": "#16A34A",
  "--pc-success-subtle": "#F0FDF4",
  "--pc-success-fg": "#FFFFFF",
  // Warning
  "--pc-warning": "#EAB308",
  "--pc-warning-hover": "#CA8A04",
  "--pc-warning-subtle": "#FEFCE8",
  "--pc-warning-fg": "#18181B",
  // Error
  "--pc-error": "#EF4444",
  "--pc-error-hover": "#DC2626",
  "--pc-error-subtle": "#FEF2F2",
  "--pc-error-fg": "#FFFFFF",
  // Info
  "--pc-info": "#3B82F6",
  "--pc-info-hover": "#2563EB",
  "--pc-info-subtle": "#EFF6FF",
  "--pc-info-fg": "#FFFFFF",
  // Input
  "--pc-input-bg": "#FFFFFF",
  "--pc-input-border": "#E4E4E7",
  "--pc-input-border-focus": "#7C3AED",
  "--pc-input-border-error": "#EF4444",
  "--pc-input-placeholder": "#A1A1AA",
  // Overlay
  "--pc-overlay": "rgba(24, 24, 27, 0.5)",
  "--pc-overlay-heavy": "rgba(24, 24, 27, 0.8)"
};
var darkColors4 = {
  // Background - True dark (default mode for SIGNAL)
  "--pc-bg-base": "#0A0A0B",
  "--pc-bg-subtle": "#141415",
  "--pc-bg-muted": "#1F1F21",
  "--pc-bg-emphasized": "#2A2A2D",
  "--pc-bg-inverted": "#FFFFFF",
  // Foreground - High contrast
  "--pc-fg-base": "#FFFFFF",
  "--pc-fg-muted": "#A1A1A6",
  "--pc-fg-subtle": "#636366",
  "--pc-fg-inverted": "#0A0A0B",
  // Primary - Electric Violet (brighter in dark)
  "--pc-primary": "#8B5CF6",
  "--pc-primary-hover": "#A78BFA",
  "--pc-primary-active": "#C4B5FD",
  "--pc-primary-subtle": "rgba(139, 92, 246, 0.15)",
  "--pc-primary-muted": "rgba(139, 92, 246, 0.25)",
  "--pc-primary-fg": "#FFFFFF",
  // Secondary
  "--pc-secondary": "#71717A",
  "--pc-secondary-hover": "#A1A1AA",
  "--pc-secondary-active": "#D4D4D8",
  "--pc-secondary-subtle": "#27272A",
  "--pc-secondary-fg": "#FFFFFF",
  // Border
  "--pc-border": "#27272A",
  "--pc-border-muted": "#1F1F21",
  "--pc-border-subtle": "#141415",
  "--pc-border-emphasized": "#3F3F46",
  "--pc-border-focus": "#8B5CF6",
  // Success - Neon Green (glowing in dark)
  "--pc-success": "#22C55E",
  "--pc-success-hover": "#4ADE80",
  "--pc-success-subtle": "rgba(34, 197, 94, 0.15)",
  "--pc-success-fg": "#0A0A0B",
  // Warning
  "--pc-warning": "#FACC15",
  "--pc-warning-hover": "#FDE047",
  "--pc-warning-subtle": "rgba(250, 204, 21, 0.15)",
  "--pc-warning-fg": "#0A0A0B",
  // Error
  "--pc-error": "#F87171",
  "--pc-error-hover": "#FCA5A5",
  "--pc-error-subtle": "rgba(248, 113, 113, 0.15)",
  "--pc-error-fg": "#0A0A0B",
  // Info
  "--pc-info": "#60A5FA",
  "--pc-info-hover": "#93C5FD",
  "--pc-info-subtle": "rgba(96, 165, 250, 0.15)",
  "--pc-info-fg": "#0A0A0B",
  // Input
  "--pc-input-bg": "#141415",
  "--pc-input-border": "#27272A",
  "--pc-input-border-focus": "#8B5CF6",
  "--pc-input-border-error": "#F87171",
  "--pc-input-placeholder": "#636366",
  // Overlay
  "--pc-overlay": "rgba(0, 0, 0, 0.7)",
  "--pc-overlay-heavy": "rgba(0, 0, 0, 0.9)"
};
var signalSpacing = {
  "--pc-space-page-x": "1.25rem",
  "--pc-space-page-y": "1.25rem",
  "--pc-space-section-gap": "1.5rem",
  "--pc-space-card-padding": "1.25rem",
  "--pc-space-card-gap": "1rem",
  "--pc-space-input-x": "0.75rem",
  "--pc-space-input-y": "0.5rem",
  "--pc-space-btn-x": "1rem",
  "--pc-space-btn-y": "0.5rem",
  "--pc-space-btn-gap": "0.5rem"
};
var signalTypography = {
  "--pc-font-sans": '"Satoshi", "Inter", ui-sans-serif, system-ui, sans-serif',
  "--pc-font-mono": '"Geist Mono", "JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
  "--pc-text-xs": "0.75rem",
  "--pc-text-sm": "0.8125rem",
  "--pc-text-base": "0.9375rem",
  "--pc-text-lg": "1.0625rem",
  "--pc-text-xl": "1.25rem",
  "--pc-text-2xl": "1.5rem",
  "--pc-text-3xl": "2rem",
  "--pc-text-h1": "2.25rem",
  "--pc-text-h2": "1.5rem",
  "--pc-text-h3": "1.125rem",
  "--pc-text-h4": "1rem",
  "--pc-weight-normal": "400",
  "--pc-weight-medium": "500",
  "--pc-weight-semibold": "600",
  "--pc-weight-bold": "700",
  "--pc-weight-h1": "700",
  "--pc-weight-h2": "600",
  "--pc-weight-h3": "600",
  "--pc-weight-h4": "500",
  "--pc-lh-xs": "1rem",
  "--pc-lh-sm": "1.25rem",
  "--pc-lh-base": "1.5rem",
  "--pc-lh-lg": "1.625rem",
  "--pc-lh-h1": "2.5rem",
  "--pc-lh-h2": "1.875rem",
  "--pc-lh-h3": "1.5rem",
  "--pc-lh-h4": "1.375rem"
};
var signalShadows = {
  "--pc-shadow-card": "0 0 0 1px rgba(255, 255, 255, 0.05)",
  "--pc-shadow-dropdown": "0 4px 16px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)",
  "--pc-shadow-modal": "0 16px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)",
  "--pc-shadow-popover": "0 8px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)",
  "--pc-shadow-toast": "0 8px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)"
};
var signalRadius = {
  "--pc-radius-btn": "0.5rem",
  "--pc-radius-input": "0.5rem",
  "--pc-radius-card": "0.75rem",
  "--pc-radius-modal": "1rem",
  "--pc-radius-badge": "0.375rem",
  "--pc-radius-full": "9999px"
};
var signalTransitions = {
  "--pc-transition-fast": "150ms cubic-bezier(0.2, 0, 0, 1)",
  "--pc-transition-normal": "200ms cubic-bezier(0.2, 0, 0, 1)",
  "--pc-transition-slow": "300ms cubic-bezier(0.2, 0, 0, 1)"
};
var signalTheme = {
  name: "signal",
  displayName: "Signal",
  colors: {
    light: lightColors4,
    dark: darkColors4
  },
  spacing: signalSpacing,
  typography: signalTypography,
  shadows: signalShadows,
  radius: signalRadius,
  transitions: signalTransitions
};
var signalFontLinks = {
  satoshi: "https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700,900&display=swap",
  inter: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
  mono: "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500&display=swap"
};

// src/themes/atlas.ts
var lightColors5 = {
  // Background - Cool Neutral
  "--pc-bg-base": "#FFFFFF",
  "--pc-bg-subtle": "#F8FAFC",
  "--pc-bg-muted": "#F1F5F9",
  "--pc-bg-emphasized": "#E2E8F0",
  "--pc-bg-inverted": "#0F172A",
  // Foreground
  "--pc-fg-base": "#0F172A",
  "--pc-fg-muted": "#475569",
  "--pc-fg-subtle": "#94A3B8",
  "--pc-fg-inverted": "#F8FAFC",
  // Primary - Professional Blue
  "--pc-primary": "#2563EB",
  "--pc-primary-hover": "#1D4ED8",
  "--pc-primary-active": "#1E40AF",
  "--pc-primary-subtle": "#EFF6FF",
  "--pc-primary-muted": "#DBEAFE",
  "--pc-primary-fg": "#FFFFFF",
  // Secondary - Slate
  "--pc-secondary": "#475569",
  "--pc-secondary-hover": "#334155",
  "--pc-secondary-active": "#1E293B",
  "--pc-secondary-subtle": "#F1F5F9",
  "--pc-secondary-fg": "#FFFFFF",
  // Border
  "--pc-border": "#E2E8F0",
  "--pc-border-muted": "#F1F5F9",
  "--pc-border-subtle": "#F8FAFC",
  "--pc-border-emphasized": "#CBD5E1",
  "--pc-border-focus": "#2563EB",
  // Success - WCAG AA compliant
  "--pc-success": "#16A34A",
  "--pc-success-hover": "#15803D",
  "--pc-success-subtle": "#F0FDF4",
  "--pc-success-fg": "#FFFFFF",
  // Warning - WCAG AA compliant
  "--pc-warning": "#CA8A04",
  "--pc-warning-hover": "#A16207",
  "--pc-warning-subtle": "#FEFCE8",
  "--pc-warning-fg": "#FFFFFF",
  // Error - WCAG AA compliant
  "--pc-error": "#DC2626",
  "--pc-error-hover": "#B91C1C",
  "--pc-error-subtle": "#FEF2F2",
  "--pc-error-fg": "#FFFFFF",
  // Info - WCAG AA compliant
  "--pc-info": "#0284C7",
  "--pc-info-hover": "#0369A1",
  "--pc-info-subtle": "#F0F9FF",
  "--pc-info-fg": "#FFFFFF",
  // Input
  "--pc-input-bg": "#FFFFFF",
  "--pc-input-border": "#CBD5E1",
  "--pc-input-border-focus": "#2563EB",
  "--pc-input-border-error": "#DC2626",
  "--pc-input-placeholder": "#94A3B8",
  // Overlay
  "--pc-overlay": "rgba(15, 23, 42, 0.5)",
  "--pc-overlay-heavy": "rgba(15, 23, 42, 0.8)"
};
var darkColors5 = {
  // Background - Slate dark
  "--pc-bg-base": "#0F172A",
  "--pc-bg-subtle": "#1E293B",
  "--pc-bg-muted": "#334155",
  "--pc-bg-emphasized": "#475569",
  "--pc-bg-inverted": "#F8FAFC",
  // Foreground
  "--pc-fg-base": "#F8FAFC",
  "--pc-fg-muted": "#CBD5E1",
  "--pc-fg-subtle": "#64748B",
  "--pc-fg-inverted": "#0F172A",
  // Primary - Lighter blue in dark
  "--pc-primary": "#60A5FA",
  "--pc-primary-hover": "#93C5FD",
  "--pc-primary-active": "#BFDBFE",
  "--pc-primary-subtle": "#1E3A5F",
  "--pc-primary-muted": "#1E40AF",
  "--pc-primary-fg": "#0F172A",
  // Secondary
  "--pc-secondary": "#94A3B8",
  "--pc-secondary-hover": "#CBD5E1",
  "--pc-secondary-active": "#E2E8F0",
  "--pc-secondary-subtle": "#1E293B",
  "--pc-secondary-fg": "#0F172A",
  // Border
  "--pc-border": "#334155",
  "--pc-border-muted": "#1E293B",
  "--pc-border-subtle": "#0F172A",
  "--pc-border-emphasized": "#475569",
  "--pc-border-focus": "#60A5FA",
  // Success
  "--pc-success": "#4ADE80",
  "--pc-success-hover": "#86EFAC",
  "--pc-success-subtle": "#052E16",
  "--pc-success-fg": "#0F172A",
  // Warning
  "--pc-warning": "#FACC15",
  "--pc-warning-hover": "#FDE047",
  "--pc-warning-subtle": "#422006",
  "--pc-warning-fg": "#0F172A",
  // Error
  "--pc-error": "#F87171",
  "--pc-error-hover": "#FCA5A5",
  "--pc-error-subtle": "#450A0A",
  "--pc-error-fg": "#0F172A",
  // Info
  "--pc-info": "#38BDF8",
  "--pc-info-hover": "#7DD3FC",
  "--pc-info-subtle": "#082F49",
  "--pc-info-fg": "#0F172A",
  // Input
  "--pc-input-bg": "#1E293B",
  "--pc-input-border": "#334155",
  "--pc-input-border-focus": "#60A5FA",
  "--pc-input-border-error": "#F87171",
  "--pc-input-placeholder": "#64748B",
  // Overlay
  "--pc-overlay": "rgba(0, 0, 0, 0.6)",
  "--pc-overlay-heavy": "rgba(0, 0, 0, 0.85)"
};
var atlasSpacing = {
  "--pc-space-page-x": "1.5rem",
  "--pc-space-page-y": "1.5rem",
  "--pc-space-section-gap": "2rem",
  "--pc-space-card-padding": "1.5rem",
  "--pc-space-card-gap": "1rem",
  "--pc-space-input-x": "0.875rem",
  "--pc-space-input-y": "0.625rem",
  "--pc-space-btn-x": "1rem",
  "--pc-space-btn-y": "0.625rem",
  "--pc-space-btn-gap": "0.5rem"
};
var atlasTypography = {
  "--pc-font-sans": '"Source Sans 3", ui-sans-serif, system-ui, sans-serif',
  "--pc-font-mono": '"Source Code Pro", ui-monospace, SFMono-Regular, monospace',
  "--pc-text-xs": "0.75rem",
  "--pc-text-sm": "0.875rem",
  "--pc-text-base": "1rem",
  "--pc-text-lg": "1.125rem",
  "--pc-text-xl": "1.25rem",
  "--pc-text-2xl": "1.5rem",
  "--pc-text-3xl": "1.875rem",
  "--pc-text-h1": "2.25rem",
  "--pc-text-h2": "1.75rem",
  "--pc-text-h3": "1.375rem",
  "--pc-text-h4": "1.125rem",
  "--pc-weight-normal": "400",
  "--pc-weight-medium": "500",
  "--pc-weight-semibold": "600",
  "--pc-weight-bold": "700",
  "--pc-weight-h1": "700",
  "--pc-weight-h2": "600",
  "--pc-weight-h3": "600",
  "--pc-weight-h4": "600",
  "--pc-lh-xs": "1rem",
  "--pc-lh-sm": "1.25rem",
  "--pc-lh-base": "1.5rem",
  "--pc-lh-lg": "1.75rem",
  "--pc-lh-h1": "2.75rem",
  "--pc-lh-h2": "2.25rem",
  "--pc-lh-h3": "1.875rem",
  "--pc-lh-h4": "1.5rem"
};
var atlasShadows = {
  "--pc-shadow-card": "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)",
  "--pc-shadow-dropdown": "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
  "--pc-shadow-modal": "0 20px 25px rgba(0, 0, 0, 0.1), 0 8px 10px rgba(0, 0, 0, 0.06)",
  "--pc-shadow-popover": "0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.06)",
  "--pc-shadow-toast": "0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.06)"
};
var atlasRadius = {
  "--pc-radius-btn": "0.375rem",
  "--pc-radius-input": "0.375rem",
  "--pc-radius-card": "0.5rem",
  "--pc-radius-modal": "0.625rem",
  "--pc-radius-badge": "0.25rem",
  "--pc-radius-full": "9999px"
};
var atlasTransitions = {
  "--pc-transition-fast": "150ms ease",
  "--pc-transition-normal": "200ms ease",
  "--pc-transition-slow": "300ms ease"
};
var atlasTheme = {
  name: "atlas",
  displayName: "Atlas",
  colors: {
    light: lightColors5,
    dark: darkColors5
  },
  spacing: atlasSpacing,
  typography: atlasTypography,
  shadows: atlasShadows,
  radius: atlasRadius,
  transitions: atlasTransitions
};
var atlasFontLink = "https://fonts.googleapis.com/css2?family=Source+Code+Pro:wght@400;500&family=Source+Sans+3:wght@400;500;600;700&display=swap";
var atlasDisplayFont = '"Outfit", ui-sans-serif, system-ui, sans-serif';

// src/themes/index.ts
var themes = {
  // Base design system (default)
  meridian: meridianTheme,
  // Design system variations
  carbon: carbonTheme,
  signal: signalTheme,
  atlas: atlasTheme,
  // Product-specific (built on Meridian with custom colors)
  arc2: arc2Theme,
  brandchord: brandchordTheme,
  mazo: mazoTheme,
  memoria: memoriaTheme
};
var designSystemThemes = {
  meridian: meridianTheme,
  carbon: carbonTheme,
  signal: signalTheme,
  atlas: atlasTheme
};
var productThemes = {
  arc2: arc2Theme,
  brandchord: brandchordTheme,
  mazo: mazoTheme,
  memoria: memoriaTheme
};
var defaultTheme = meridianTheme;

// src/components/ThemeProvider.tsx
import { jsx } from "react/jsx-runtime";
var ThemeContext = createContext(null);
function getSystemMode() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function generateCSSVariables(theme, mode) {
  const colorVars = theme.colors[mode];
  const lines = [];
  for (const [key, value] of Object.entries(colorVars)) {
    lines.push(`${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(theme.spacing)) {
    lines.push(`${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(theme.typography)) {
    lines.push(`${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(theme.shadows)) {
    lines.push(`${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(theme.radius)) {
    lines.push(`${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(theme.transitions)) {
    lines.push(`${key}: ${value};`);
  }
  return lines.join("\n");
}
function ThemeProvider({
  children,
  defaultTheme: initialTheme = "arc2",
  defaultMode: initialMode = "system",
  storageKey = "powercore-theme"
}) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return initialTheme;
    return localStorage.getItem(`${storageKey}-name`) || initialTheme;
  });
  const [mode, setModeState] = useState(() => {
    if (typeof window === "undefined") return initialMode;
    return localStorage.getItem(`${storageKey}-mode`) || initialMode;
  });
  const [resolvedMode, setResolvedMode] = useState(() => {
    if (mode === "system") return getSystemMode();
    return mode;
  });
  useEffect(() => {
    if (mode !== "system") {
      setResolvedMode(mode);
      return;
    }
    setResolvedMode(getSystemMode());
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => {
      setResolvedMode(e.matches ? "dark" : "light");
    };
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [mode]);
  useEffect(() => {
    const themeConfig = themes[theme] || defaultTheme;
    const cssVars = generateCSSVariables(themeConfig, resolvedMode);
    let styleEl = document.getElementById("powercore-theme-vars");
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = "powercore-theme-vars";
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `:root {
${cssVars}
}`;
    document.documentElement.setAttribute("data-pc-theme", theme);
    document.documentElement.setAttribute("data-pc-mode", resolvedMode);
  }, [theme, resolvedMode]);
  const setTheme = useCallback(
    (newTheme) => {
      if (!themes[newTheme]) {
        console.warn(
          `Theme "${newTheme}" not found. Available themes: ${Object.keys(themes).join(", ")}`
        );
        return;
      }
      setThemeState(newTheme);
      localStorage.setItem(`${storageKey}-name`, newTheme);
    },
    [storageKey]
  );
  const setMode = useCallback(
    (newMode) => {
      setModeState(newMode);
      localStorage.setItem(`${storageKey}-mode`, newMode);
    },
    [storageKey]
  );
  const value = useMemo(
    () => ({
      theme,
      mode,
      resolvedMode,
      setTheme,
      setMode
    }),
    [theme, mode, resolvedMode, setTheme, setMode]
  );
  return /* @__PURE__ */ jsx(ThemeContext.Provider, { value, children });
}
function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

// src/components/Button.tsx
import { forwardRef } from "react";

// src/utils/cn.ts
function cn(...inputs) {
  return inputs.filter(Boolean).join(" ");
}

// src/components/Button.tsx
import { Fragment, jsx as jsx2, jsxs } from "react/jsx-runtime";
var variantClasses = {
  primary: "pc-button--primary",
  secondary: "pc-button--secondary",
  outline: "pc-button--outline",
  ghost: "pc-button--ghost",
  destructive: "pc-button--destructive"
};
var sizeClasses = {
  sm: "pc-button--sm",
  md: "",
  lg: "pc-button--lg",
  icon: "pc-button--icon"
};
var Button = forwardRef(
  ({
    className,
    variant = "primary",
    size = "md",
    children,
    leftIcon,
    rightIcon,
    loading,
    disabled,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx2(
      "button",
      {
        ref,
        className: cn("pc-button", variantClasses[variant], sizeClasses[size], className),
        disabled: disabled || loading,
        ...props,
        children: loading ? /* @__PURE__ */ jsx2(LoadingSpinner, {}) : /* @__PURE__ */ jsxs(Fragment, { children: [
          leftIcon,
          children,
          rightIcon
        ] })
      }
    );
  }
);
Button.displayName = "Button";
function LoadingSpinner() {
  return /* @__PURE__ */ jsx2(
    "svg",
    {
      className: "pc-button__spinner",
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      style: { animation: "spin 1s linear infinite" },
      children: /* @__PURE__ */ jsx2("path", { d: "M21 12a9 9 0 1 1-6.219-8.56" })
    }
  );
}

// src/components/Input.tsx
import { forwardRef as forwardRef2 } from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var Input = forwardRef2(
  ({ className, error, type = "text", ...props }, ref) => {
    return /* @__PURE__ */ jsx3(
      "input",
      {
        ref,
        type,
        className: cn("pc-input", error && "pc-input--error", className),
        ...props
      }
    );
  }
);
Input.displayName = "Input";

// src/components/Textarea.tsx
import { forwardRef as forwardRef3 } from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var Textarea = forwardRef3(
  ({ className, error, ...props }, ref) => {
    return /* @__PURE__ */ jsx4(
      "textarea",
      {
        ref,
        className: cn("pc-input", "pc-textarea", error && "pc-input--error", className),
        ...props
      }
    );
  }
);
Textarea.displayName = "Textarea";

// src/components/Select.tsx
import { forwardRef as forwardRef4 } from "react";
import { jsx as jsx5 } from "react/jsx-runtime";
var Select = forwardRef4(
  ({ className, error, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx5(
      "select",
      {
        ref,
        className: cn("pc-input", "pc-select", error && "pc-input--error", className),
        ...props,
        children
      }
    );
  }
);
Select.displayName = "Select";

// src/components/Checkbox.tsx
import { forwardRef as forwardRef5 } from "react";
import { jsx as jsx6, jsxs as jsxs2 } from "react/jsx-runtime";
var Checkbox = forwardRef5(
  ({ className, label, id, ...props }, ref) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).slice(2, 9)}`;
    if (label) {
      return /* @__PURE__ */ jsxs2("label", { className: "pc-checkbox-wrapper", htmlFor: checkboxId, children: [
        /* @__PURE__ */ jsx6(
          "input",
          {
            ref,
            id: checkboxId,
            type: "checkbox",
            className: cn("pc-checkbox", className),
            ...props
          }
        ),
        /* @__PURE__ */ jsx6("span", { children: label })
      ] });
    }
    return /* @__PURE__ */ jsx6(
      "input",
      {
        ref,
        id: checkboxId,
        type: "checkbox",
        className: cn("pc-checkbox", className),
        ...props
      }
    );
  }
);
Checkbox.displayName = "Checkbox";

// src/components/Switch.tsx
import { forwardRef as forwardRef6 } from "react";
import { jsx as jsx7, jsxs as jsxs3 } from "react/jsx-runtime";
var Switch = forwardRef6(
  ({ className, label, id, ...props }, ref) => {
    const switchId = id || `switch-${Math.random().toString(36).slice(2, 9)}`;
    if (label) {
      return /* @__PURE__ */ jsxs3("label", { className: "pc-switch-wrapper", htmlFor: switchId, children: [
        /* @__PURE__ */ jsx7(
          "input",
          {
            ref,
            id: switchId,
            type: "checkbox",
            role: "switch",
            className: cn("pc-switch", className),
            ...props
          }
        ),
        /* @__PURE__ */ jsx7("span", { children: label })
      ] });
    }
    return /* @__PURE__ */ jsx7(
      "input",
      {
        ref,
        id: switchId,
        type: "checkbox",
        role: "switch",
        className: cn("pc-switch", className),
        ...props
      }
    );
  }
);
Switch.displayName = "Switch";

// src/components/Card.tsx
import { forwardRef as forwardRef7 } from "react";
import { jsx as jsx8 } from "react/jsx-runtime";
var Card = forwardRef7(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx8("div", { ref, className: cn("pc-card", className), ...props, children });
  }
);
Card.displayName = "Card";
var CardHeader = forwardRef7(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx8("div", { ref, className: cn("pc-card__header", className), ...props, children });
  }
);
CardHeader.displayName = "CardHeader";
var CardContent = forwardRef7(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx8("div", { ref, className: cn("pc-card__content", className), ...props, children });
  }
);
CardContent.displayName = "CardContent";
var CardFooter = forwardRef7(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx8("div", { ref, className: cn("pc-card__footer", className), ...props, children });
  }
);
CardFooter.displayName = "CardFooter";
var CardTitle = forwardRef7(
  ({ className, children, as: Component = "h3", ...props }, ref) => {
    return /* @__PURE__ */ jsx8(
      Component,
      {
        ref,
        className: cn("pc-card__title", className),
        style: {
          fontSize: "var(--pc-text-lg)",
          fontWeight: "var(--pc-weight-semibold)",
          color: "var(--pc-fg-base)",
          margin: 0
        },
        ...props,
        children
      }
    );
  }
);
CardTitle.displayName = "CardTitle";
var CardDescription = forwardRef7(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx8(
      "p",
      {
        ref,
        className: cn("pc-card__description", className),
        style: {
          fontSize: "var(--pc-text-sm)",
          color: "var(--pc-fg-muted)",
          margin: "0.25rem 0 0 0"
        },
        ...props,
        children
      }
    );
  }
);
CardDescription.displayName = "CardDescription";

// src/components/Badge.tsx
import { forwardRef as forwardRef8 } from "react";
import { jsx as jsx9 } from "react/jsx-runtime";
var variantClasses2 = {
  default: "pc-badge--default",
  primary: "pc-badge--primary",
  secondary: "pc-badge--secondary",
  success: "pc-badge--success",
  warning: "pc-badge--warning",
  error: "pc-badge--error",
  info: "pc-badge--info"
};
var Badge = forwardRef8(
  ({ className, variant = "default", children, ...props }, ref) => {
    return /* @__PURE__ */ jsx9("span", { ref, className: cn("pc-badge", variantClasses2[variant], className), ...props, children });
  }
);
Badge.displayName = "Badge";

// src/components/Tabs.tsx
import { createContext as createContext2, useContext as useContext2, useState as useState2 } from "react";
import { jsx as jsx10 } from "react/jsx-runtime";
var TabsContext = createContext2(null);
function useTabsContext() {
  const context = useContext2(TabsContext);
  if (!context) {
    throw new Error("Tabs components must be used within a Tabs provider");
  }
  return context;
}
function Tabs({ defaultValue, children, className, onValueChange, ...props }) {
  const [activeTab, setActiveTabState] = useState2(defaultValue);
  const setActiveTab = (value) => {
    setActiveTabState(value);
    onValueChange?.(value);
  };
  return /* @__PURE__ */ jsx10(TabsContext.Provider, { value: { activeTab, setActiveTab }, children: /* @__PURE__ */ jsx10("div", { className: cn("pc-tabs", className), ...props, children }) });
}
function TabsList({ children, className, ...props }) {
  return /* @__PURE__ */ jsx10("div", { role: "tablist", className: cn("pc-tabs__list", className), ...props, children });
}
function TabsTrigger({ value, children, className, disabled, ...props }) {
  const { activeTab, setActiveTab } = useTabsContext();
  const isActive = activeTab === value;
  return /* @__PURE__ */ jsx10(
    "button",
    {
      role: "tab",
      type: "button",
      "aria-selected": isActive,
      disabled,
      className: cn("pc-tabs__trigger", isActive && "pc-tabs__trigger--active", className),
      onClick: () => setActiveTab(value),
      ...props,
      children
    }
  );
}
function TabsContent({ value, children, className, ...props }) {
  const { activeTab } = useTabsContext();
  if (activeTab !== value) {
    return null;
  }
  return /* @__PURE__ */ jsx10("div", { role: "tabpanel", className: cn("pc-tabs__content", className), ...props, children });
}

// src/components/Dialog.tsx
import {
  createContext as createContext3,
  useCallback as useCallback2,
  useContext as useContext3,
  useEffect as useEffect2,
  useState as useState3
} from "react";
import { createPortal } from "react-dom";
import { jsx as jsx11 } from "react/jsx-runtime";
var DialogContext = createContext3(null);
function useDialogContext() {
  const context = useContext3(DialogContext);
  if (!context) {
    throw new Error("Dialog components must be used within a Dialog provider");
  }
  return context;
}
function Dialog({
  children,
  open: controlledOpen,
  onOpenChange,
  defaultOpen = false
}) {
  const [internalOpen, setInternalOpen] = useState3(defaultOpen);
  const isControlled = controlledOpen !== void 0;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = useCallback2(
    (newOpen) => {
      if (!isControlled) {
        setInternalOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [isControlled, onOpenChange]
  );
  return /* @__PURE__ */ jsx11(DialogContext.Provider, { value: { open, setOpen }, children });
}
function DialogTrigger({ children, className, asChild, ...props }) {
  const { setOpen } = useDialogContext();
  return /* @__PURE__ */ jsx11("button", { type: "button", className, onClick: () => setOpen(true), ...props, children });
}
function DialogContent({ children, className, ...props }) {
  const { open, setOpen } = useDialogContext();
  useEffect2(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      const handleEscape = (e) => {
        if (e.key === "Escape") {
          setOpen(false);
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.body.style.overflow = "";
        document.removeEventListener("keydown", handleEscape);
      };
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, setOpen]);
  if (!open) return null;
  return createPortal(
    /* @__PURE__ */ jsx11(
      "div",
      {
        className: "pc-dialog-overlay",
        onClick: (e) => {
          if (e.target === e.currentTarget) {
            setOpen(false);
          }
        },
        children: /* @__PURE__ */ jsx11("div", { role: "dialog", "aria-modal": "true", className: cn("pc-dialog", className), ...props, children })
      }
    ),
    document.body
  );
}
function DialogHeader({ children, className, ...props }) {
  return /* @__PURE__ */ jsx11("div", { className: cn("pc-dialog__header", className), ...props, children });
}
function DialogTitle({ children, className, ...props }) {
  return /* @__PURE__ */ jsx11("h2", { className: cn("pc-dialog__title", className), ...props, children });
}
function DialogDescription({ children, className, ...props }) {
  return /* @__PURE__ */ jsx11(
    "p",
    {
      className,
      style: {
        fontSize: "var(--pc-text-sm)",
        color: "var(--pc-fg-muted)",
        margin: "0.25rem 0 0 0"
      },
      ...props,
      children
    }
  );
}
function DialogClose({ children, className, ...props }) {
  const { setOpen } = useDialogContext();
  return /* @__PURE__ */ jsx11(
    "button",
    {
      type: "button",
      className: cn("pc-dialog__close", className),
      onClick: () => setOpen(false),
      "aria-label": "Close",
      ...props,
      children: children || /* @__PURE__ */ jsx11(
        "svg",
        {
          width: "16",
          height: "16",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          children: /* @__PURE__ */ jsx11("path", { d: "M18 6L6 18M6 6l12 12" })
        }
      )
    }
  );
}
function DialogBody({ children, className, ...props }) {
  return /* @__PURE__ */ jsx11("div", { className: cn("pc-dialog__content", className), ...props, children });
}
function DialogFooter({ children, className, ...props }) {
  return /* @__PURE__ */ jsx11("div", { className: cn("pc-dialog__footer", className), ...props, children });
}

// src/components/Toast.tsx
import { createContext as createContext4, useCallback as useCallback3, useContext as useContext4, useEffect as useEffect3, useState as useState4 } from "react";
import { createPortal as createPortal2 } from "react-dom";
import { jsx as jsx12, jsxs as jsxs4 } from "react/jsx-runtime";
var ToastContext = createContext4(null);
function useToast() {
  const context = useContext4(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
function ToastProvider({ children }) {
  const [toasts, setToasts] = useState4([]);
  const addToast = useCallback3((toast) => {
    const id = Math.random().toString(36).slice(2, 9);
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);
  const removeToast = useCallback3((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  return /* @__PURE__ */ jsxs4(ToastContext.Provider, { value: { toasts, addToast, removeToast }, children: [
    children,
    /* @__PURE__ */ jsx12(ToastContainer, {})
  ] });
}
function ToastContainer() {
  const { toasts, removeToast } = useToast();
  if (typeof document === "undefined") return null;
  return createPortal2(
    /* @__PURE__ */ jsx12("div", { className: "pc-toast-container", children: toasts.map((toast) => /* @__PURE__ */ jsx12(ToastItem, { toast, onClose: () => removeToast(toast.id) }, toast.id)) }),
    document.body
  );
}
function ToastItem({ toast, onClose }) {
  const { variant = "default", title, description, duration = 5e3 } = toast;
  useEffect3(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);
  const variantClass = variant !== "default" ? `pc-toast--${variant}` : "";
  return /* @__PURE__ */ jsxs4("div", { className: cn("pc-toast", variantClass), children: [
    variant !== "default" && /* @__PURE__ */ jsx12(ToastIcon, { variant }),
    /* @__PURE__ */ jsxs4("div", { className: "pc-toast__content", children: [
      title && /* @__PURE__ */ jsx12("div", { className: "pc-toast__title", children: title }),
      description && /* @__PURE__ */ jsx12("div", { className: "pc-toast__description", children: description })
    ] }),
    /* @__PURE__ */ jsx12("button", { className: "pc-toast__close", onClick: onClose, "aria-label": "Close", children: /* @__PURE__ */ jsx12(
      "svg",
      {
        width: "14",
        height: "14",
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: "2",
        children: /* @__PURE__ */ jsx12("path", { d: "M18 6L6 18M6 6l12 12" })
      }
    ) })
  ] });
}
function ToastIcon({ variant }) {
  const iconStyle = {
    width: 20,
    height: 20,
    flexShrink: 0
  };
  const getColor = () => {
    switch (variant) {
      case "success":
        return "var(--pc-success)";
      case "warning":
        return "var(--pc-warning)";
      case "error":
        return "var(--pc-error)";
      case "info":
        return "var(--pc-info)";
      default:
        return "var(--pc-fg-muted)";
    }
  };
  const getPath = () => {
    switch (variant) {
      case "success":
        return /* @__PURE__ */ jsx12("path", { d: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" });
      case "warning":
        return /* @__PURE__ */ jsx12("path", { d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" });
      case "error":
        return /* @__PURE__ */ jsx12("path", { d: "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" });
      case "info":
        return /* @__PURE__ */ jsx12("path", { d: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" });
      default:
        return null;
    }
  };
  return /* @__PURE__ */ jsx12(
    "svg",
    {
      style: iconStyle,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: getColor(),
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: getPath()
    }
  );
}

// src/components/Table.tsx
import {
  forwardRef as forwardRef9
} from "react";
import { jsx as jsx13 } from "react/jsx-runtime";
var Table = forwardRef9(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx13("div", { className: "pc-table-wrapper", children: /* @__PURE__ */ jsx13("table", { ref, className: cn("pc-table", className), ...props }) });
});
Table.displayName = "Table";
var TableHeader = forwardRef9(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx13("thead", { ref, className, ...props });
  }
);
TableHeader.displayName = "TableHeader";
var TableBody = forwardRef9(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx13("tbody", { ref, className, ...props });
  }
);
TableBody.displayName = "TableBody";
var TableFooter = forwardRef9(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx13("tfoot", { ref, className, ...props });
  }
);
TableFooter.displayName = "TableFooter";
var TableRow = forwardRef9(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx13("tr", { ref, className, ...props });
  }
);
TableRow.displayName = "TableRow";
var TableHead = forwardRef9(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx13("th", { ref, className, ...props });
  }
);
TableHead.displayName = "TableHead";
var TableCell = forwardRef9(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx13("td", { ref, className, ...props });
  }
);
TableCell.displayName = "TableCell";
var TableCaption = forwardRef9(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx13(
      "caption",
      {
        ref,
        className,
        style: {
          padding: "0.75rem",
          fontSize: "var(--pc-text-sm)",
          color: "var(--pc-fg-muted)",
          captionSide: "bottom"
        },
        ...props
      }
    );
  }
);
TableCaption.displayName = "TableCaption";

// src/components/DropdownMenu.tsx
import {
  createContext as createContext5,
  useContext as useContext5,
  useEffect as useEffect4,
  useRef,
  useState as useState5
} from "react";
import { createPortal as createPortal3 } from "react-dom";
import { jsx as jsx14 } from "react/jsx-runtime";
var DropdownContext = createContext5(null);
function useDropdownContext() {
  const context = useContext5(DropdownContext);
  if (!context) {
    throw new Error("DropdownMenu components must be used within a DropdownMenu provider");
  }
  return context;
}
function DropdownMenu({ children }) {
  const [open, setOpen] = useState5(false);
  const triggerRef = useRef(null);
  return /* @__PURE__ */ jsx14(DropdownContext.Provider, { value: { open, setOpen, triggerRef }, children: /* @__PURE__ */ jsx14("div", { className: "pc-dropdown", children }) });
}
function DropdownMenuTrigger({
  children,
  className,
  asChild,
  onClick,
  ...props
}) {
  const { open, setOpen, triggerRef } = useDropdownContext();
  const handleClick = (e) => {
    e.stopPropagation();
    setOpen(!open);
    onClick?.(e);
  };
  return /* @__PURE__ */ jsx14(
    "button",
    {
      ref: triggerRef,
      type: "button",
      "aria-expanded": open,
      "aria-haspopup": "menu",
      className,
      onClick: handleClick,
      ...props,
      children
    }
  );
}
function DropdownMenuContent({
  children,
  className,
  align = "start",
  sideOffset = 4,
  ...props
}) {
  const { open, setOpen, triggerRef } = useDropdownContext();
  const contentRef = useRef(null);
  const [position, setPosition] = useState5({ top: 0, left: 0 });
  useEffect4(() => {
    if (!open || !triggerRef.current) return;
    const updatePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const contentWidth = contentRef.current?.offsetWidth || 200;
      let left = rect.left;
      if (align === "end") {
        left = rect.right - contentWidth;
      } else if (align === "center") {
        left = rect.left + rect.width / 2 - contentWidth / 2;
      }
      const maxLeft = window.innerWidth - contentWidth - 8;
      left = Math.max(8, Math.min(left, maxLeft));
      setPosition({
        top: rect.bottom + sideOffset,
        left
      });
    };
    updatePosition();
    requestAnimationFrame(updatePosition);
  }, [open, align, sideOffset, triggerRef]);
  useEffect4(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (contentRef.current && !contentRef.current.contains(e.target) && triggerRef.current && !triggerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, setOpen, triggerRef]);
  if (!open || typeof document === "undefined") return null;
  return createPortal3(
    /* @__PURE__ */ jsx14(
      "div",
      {
        ref: contentRef,
        role: "menu",
        className: cn("pc-dropdown__content", className),
        style: {
          position: "fixed",
          top: position.top,
          left: position.left
        },
        ...props,
        children
      }
    ),
    document.body
  );
}
function DropdownMenuItem({
  children,
  className,
  destructive,
  onClick,
  ...props
}) {
  const { setOpen } = useDropdownContext();
  return /* @__PURE__ */ jsx14(
    "button",
    {
      role: "menuitem",
      className: cn(
        "pc-dropdown__item",
        destructive && "pc-dropdown__item--destructive",
        className
      ),
      onClick: (e) => {
        onClick?.(e);
        setOpen(false);
      },
      ...props,
      children
    }
  );
}
function DropdownMenuLabel({ children, className, ...props }) {
  return /* @__PURE__ */ jsx14(
    "div",
    {
      className,
      style: {
        padding: "0.5rem 0.75rem",
        fontSize: "var(--pc-text-xs)",
        fontWeight: "var(--pc-weight-semibold)",
        color: "var(--pc-fg-muted)"
      },
      ...props,
      children
    }
  );
}
function DropdownMenuSeparator({ className, ...props }) {
  return /* @__PURE__ */ jsx14("div", { className: cn("pc-dropdown__separator", className), ...props });
}

// src/components/Tooltip.tsx
import { useEffect as useEffect5, useRef as useRef2, useState as useState6 } from "react";
import { createPortal as createPortal4 } from "react-dom";
import { Fragment as Fragment2, jsx as jsx15, jsxs as jsxs5 } from "react/jsx-runtime";
function Tooltip({ children, content, delayDuration = 200, side = "top" }) {
  const [open, setOpen] = useState6(false);
  const [position, setPosition] = useState6({ top: 0, left: 0 });
  const triggerRef = useRef2(null);
  const timeoutRef = useRef2(void 0);
  const showTooltip = () => {
    timeoutRef.current = setTimeout(() => {
      setOpen(true);
    }, delayDuration);
  };
  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setOpen(false);
  };
  useEffect5(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipHeight = 32;
      const tooltipOffset = 8;
      let top;
      let left;
      switch (side) {
        case "bottom":
          top = rect.bottom + tooltipOffset;
          left = rect.left + rect.width / 2;
          break;
        case "left":
          top = rect.top + rect.height / 2;
          left = rect.left - tooltipOffset;
          break;
        case "right":
          top = rect.top + rect.height / 2;
          left = rect.right + tooltipOffset;
          break;
        case "top":
        default:
          top = rect.top - tooltipHeight - tooltipOffset;
          left = rect.left + rect.width / 2;
          break;
      }
      setPosition({ top, left });
    }
  }, [open, side]);
  useEffect5(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  return /* @__PURE__ */ jsxs5(Fragment2, { children: [
    /* @__PURE__ */ jsx15(
      "span",
      {
        ref: triggerRef,
        onMouseEnter: showTooltip,
        onMouseLeave: hideTooltip,
        onFocus: showTooltip,
        onBlur: hideTooltip,
        style: { display: "inline-block" },
        children
      }
    ),
    open && typeof document !== "undefined" && createPortal4(
      /* @__PURE__ */ jsx15(TooltipContent, { side, style: { top: position.top, left: position.left }, children: content }),
      document.body
    )
  ] });
}
function TooltipContent({ children, className, side, style, ...props }) {
  const getTransform = () => {
    switch (side) {
      case "bottom":
        return "translateX(-50%)";
      case "left":
        return "translate(-100%, -50%)";
      case "right":
        return "translateY(-50%)";
      case "top":
      default:
        return "translateX(-50%)";
    }
  };
  const getArrowStyles = () => {
    const base = {
      content: '""',
      position: "absolute",
      border: "4px solid transparent"
    };
    switch (side) {
      case "bottom":
        return {
          ...base,
          bottom: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          borderBottomColor: "var(--pc-bg-inverted)"
        };
      case "left":
        return {
          ...base,
          left: "100%",
          top: "50%",
          transform: "translateY(-50%)",
          borderLeftColor: "var(--pc-bg-inverted)"
        };
      case "right":
        return {
          ...base,
          right: "100%",
          top: "50%",
          transform: "translateY(-50%)",
          borderRightColor: "var(--pc-bg-inverted)"
        };
      case "top":
      default:
        return {
          ...base,
          top: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          borderTopColor: "var(--pc-bg-inverted)"
        };
    }
  };
  return /* @__PURE__ */ jsxs5(
    "div",
    {
      role: "tooltip",
      className: cn("pc-tooltip__content", className),
      style: {
        position: "fixed",
        transform: getTransform(),
        zIndex: 1700,
        padding: "0.375rem 0.75rem",
        backgroundColor: "var(--pc-bg-inverted)",
        color: "var(--pc-fg-inverted)",
        fontSize: "var(--pc-text-xs)",
        borderRadius: "var(--pc-radius-btn)",
        whiteSpace: "nowrap",
        animation: "pc-fade-in 100ms ease",
        ...style
      },
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx15("span", { style: getArrowStyles() })
      ]
    }
  );
}

// src/components/Form.tsx
import { forwardRef as forwardRef10 } from "react";
import { jsx as jsx16 } from "react/jsx-runtime";
var FormField = forwardRef10(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx16("div", { ref, className: cn("pc-form-field", className), ...props, children });
  }
);
FormField.displayName = "FormField";
var FormLabel = forwardRef10(
  ({ className, children, required, ...props }, ref) => {
    return /* @__PURE__ */ jsx16(
      "label",
      {
        ref,
        className: cn("pc-form-label", required && "pc-form-label--required", className),
        ...props,
        children
      }
    );
  }
);
FormLabel.displayName = "FormLabel";
var FormDescription = forwardRef10(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx16("p", { ref, className: cn("pc-form-description", className), ...props, children });
  }
);
FormDescription.displayName = "FormDescription";
var FormError = forwardRef10(
  ({ className, children, ...props }, ref) => {
    if (!children) return null;
    return /* @__PURE__ */ jsx16("p", { ref, className: cn("pc-form-error", className), role: "alert", ...props, children });
  }
);
FormError.displayName = "FormError";
var Form = forwardRef10(
  ({ className, children, onSubmit, ...props }, ref) => {
    return /* @__PURE__ */ jsx16(
      "form",
      {
        ref,
        className,
        onSubmit: (e) => {
          e.preventDefault();
          onSubmit?.(e);
        },
        ...props,
        children
      }
    );
  }
);
Form.displayName = "Form";

// src/components/FontLoader.tsx
import { useEffect as useEffect6 } from "react";
var fontConfigs = {
  carbon: {
    preconnect: ["https://fonts.googleapis.com", "https://fonts.gstatic.com"],
    links: [
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600;700&display=swap"
    ]
  },
  meridian: {
    preconnect: ["https://fonts.googleapis.com", "https://fonts.gstatic.com"],
    links: [
      "https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&display=swap"
    ]
  },
  signal: {
    preconnect: ["https://fonts.googleapis.com", "https://fonts.gstatic.com", "https://api.fontshare.com"],
    links: [
      "https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700,900&display=swap",
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
    ]
  },
  atlas: {
    preconnect: ["https://fonts.googleapis.com", "https://fonts.gstatic.com"],
    links: [
      "https://fonts.googleapis.com/css2?family=Source+Code+Pro:wght@400;500&family=Source+Sans+3:wght@400;500;600;700&display=swap"
    ]
  },
  // Product-specific themes use system fonts by default, but can be customized
  arc2: {
    preconnect: ["https://fonts.googleapis.com", "https://fonts.gstatic.com"],
    links: [
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
    ]
  },
  mazo: {
    preconnect: ["https://fonts.googleapis.com", "https://fonts.gstatic.com"],
    links: [
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
    ]
  },
  brandchord: {
    preconnect: ["https://fonts.googleapis.com", "https://fonts.gstatic.com"],
    links: [
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
    ]
  }
};
function FontLoader({ theme, additionalFonts = [] }) {
  useEffect6(() => {
    const config = fontConfigs[theme];
    if (!config) return;
    const addedElements = [];
    config.preconnect?.forEach((href) => {
      const existing = document.querySelector(`link[rel="preconnect"][href="${href}"]`);
      if (!existing) {
        const link = document.createElement("link");
        link.rel = "preconnect";
        link.href = href;
        if (href.includes("gstatic")) {
          link.crossOrigin = "anonymous";
        }
        document.head.appendChild(link);
        addedElements.push(link);
      }
    });
    const allLinks = [...config.links, ...additionalFonts];
    allLinks.forEach((href) => {
      const existing = document.querySelector(`link[href="${href}"]`);
      if (!existing) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        document.head.appendChild(link);
        addedElements.push(link);
      }
    });
    return () => {
    };
  }, [theme, additionalFonts]);
  return null;
}
function getFontLinkTags(theme) {
  const config = fontConfigs[theme];
  if (!config) return "";
  const preconnects = config.preconnect?.map((href) => {
    const crossorigin = href.includes("gstatic") ? " crossorigin" : "";
    return `<link rel="preconnect" href="${href}"${crossorigin}>`;
  }).join("\n") || "";
  const links = config.links.map(
    (href) => `<link rel="stylesheet" href="${href}">`
  ).join("\n");
  return `${preconnects}
${links}`;
}
function getFontConfig(theme) {
  return fontConfigs[theme];
}

// src/tokens/base.ts
var colors = {
  // Neutral palette
  neutral: {
    0: "#ffffff",
    50: "#fafafa",
    100: "#f4f4f5",
    200: "#e4e4e7",
    300: "#d4d4d8",
    400: "#a1a1aa",
    500: "#71717a",
    600: "#52525b",
    700: "#3f3f46",
    800: "#27272a",
    900: "#18181b",
    950: "#09090b"
  },
  // Primary colors for each theme
  indigo: {
    50: "#eef2ff",
    100: "#e0e7ff",
    200: "#c7d2fe",
    300: "#a5b4fc",
    400: "#818cf8",
    500: "#6366f1",
    600: "#4f46e5",
    700: "#4338ca",
    800: "#3730a3",
    900: "#312e81",
    950: "#1e1b4b"
  },
  violet: {
    50: "#f5f3ff",
    100: "#ede9fe",
    200: "#ddd6fe",
    300: "#c4b5fd",
    400: "#a78bfa",
    500: "#8b5cf6",
    600: "#7c3aed",
    700: "#6d28d9",
    800: "#5b21b6",
    900: "#4c1d95",
    950: "#2e1065"
  },
  emerald: {
    50: "#ecfdf5",
    100: "#d1fae5",
    200: "#a7f3d0",
    300: "#6ee7b7",
    400: "#34d399",
    500: "#10b981",
    600: "#059669",
    700: "#047857",
    800: "#065f46",
    900: "#064e3b",
    950: "#022c22"
  },
  amber: {
    50: "#fffbeb",
    100: "#fef3c7",
    200: "#fde68a",
    300: "#fcd34d",
    400: "#fbbf24",
    500: "#f59e0b",
    600: "#d97706",
    700: "#b45309",
    800: "#92400e",
    900: "#78350f",
    950: "#451a03"
  },
  rose: {
    50: "#fff1f2",
    100: "#ffe4e6",
    200: "#fecdd3",
    300: "#fda4af",
    400: "#fb7185",
    500: "#f43f5e",
    600: "#e11d48",
    700: "#be123c",
    800: "#9f1239",
    900: "#881337",
    950: "#4c0519"
  },
  sky: {
    50: "#f0f9ff",
    100: "#e0f2fe",
    200: "#bae6fd",
    300: "#7dd3fc",
    400: "#38bdf8",
    500: "#0ea5e9",
    600: "#0284c7",
    700: "#0369a1",
    800: "#075985",
    900: "#0c4a6e",
    950: "#082f49"
  },
  // Semantic status colors
  red: {
    50: "#fef2f2",
    100: "#fee2e2",
    200: "#fecaca",
    300: "#fca5a5",
    400: "#f87171",
    500: "#ef4444",
    600: "#dc2626",
    700: "#b91c1c",
    800: "#991b1b",
    900: "#7f1d1d",
    950: "#450a0a"
  },
  green: {
    50: "#f0fdf4",
    100: "#dcfce7",
    200: "#bbf7d0",
    300: "#86efac",
    400: "#4ade80",
    500: "#22c55e",
    600: "#16a34a",
    700: "#15803d",
    800: "#166534",
    900: "#14532d",
    950: "#052e16"
  },
  yellow: {
    50: "#fefce8",
    100: "#fef9c3",
    200: "#fef08a",
    300: "#fde047",
    400: "#facc15",
    500: "#eab308",
    600: "#ca8a04",
    700: "#a16207",
    800: "#854d0e",
    900: "#713f12",
    950: "#422006"
  },
  blue: {
    50: "#eff6ff",
    100: "#dbeafe",
    200: "#bfdbfe",
    300: "#93c5fd",
    400: "#60a5fa",
    500: "#3b82f6",
    600: "#2563eb",
    700: "#1d4ed8",
    800: "#1e40af",
    900: "#1e3a8a",
    950: "#172554"
  }
};
var spacing = {
  0: "0px",
  px: "1px",
  0.5: "0.125rem",
  1: "0.25rem",
  1.5: "0.375rem",
  2: "0.5rem",
  2.5: "0.625rem",
  3: "0.75rem",
  3.5: "0.875rem",
  4: "1rem",
  5: "1.25rem",
  6: "1.5rem",
  7: "1.75rem",
  8: "2rem",
  9: "2.25rem",
  10: "2.5rem",
  11: "2.75rem",
  12: "3rem",
  14: "3.5rem",
  16: "4rem",
  20: "5rem",
  24: "6rem",
  28: "7rem",
  32: "8rem",
  36: "9rem",
  40: "10rem",
  44: "11rem",
  48: "12rem",
  52: "13rem",
  56: "14rem",
  60: "15rem",
  64: "16rem",
  72: "18rem",
  80: "20rem",
  96: "24rem"
};
var typography = {
  fontFamily: {
    sans: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    serif: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
    mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace'
  },
  fontSize: {
    xs: ["0.75rem", { lineHeight: "1rem" }],
    sm: ["0.875rem", { lineHeight: "1.25rem" }],
    base: ["1rem", { lineHeight: "1.5rem" }],
    lg: ["1.125rem", { lineHeight: "1.75rem" }],
    xl: ["1.25rem", { lineHeight: "1.75rem" }],
    "2xl": ["1.5rem", { lineHeight: "2rem" }],
    "3xl": ["1.875rem", { lineHeight: "2.25rem" }],
    "4xl": ["2.25rem", { lineHeight: "2.5rem" }],
    "5xl": ["3rem", { lineHeight: "1" }],
    "6xl": ["3.75rem", { lineHeight: "1" }]
  },
  fontWeight: {
    thin: "100",
    extralight: "200",
    light: "300",
    normal: "400",
    medium: "500",
    semibold: "600",
    bold: "700",
    extrabold: "800",
    black: "900"
  },
  letterSpacing: {
    tighter: "-0.05em",
    tight: "-0.025em",
    normal: "0em",
    wide: "0.025em",
    wider: "0.05em",
    widest: "0.1em"
  }
};
var shadows = {
  none: "none",
  sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  base: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
  "2xl": "0 25px 50px -12px rgb(0 0 0 / 0.25)",
  inner: "inset 0 2px 4px 0 rgb(0 0 0 / 0.05)"
};
var radii = {
  none: "0px",
  sm: "0.125rem",
  base: "0.25rem",
  md: "0.375rem",
  lg: "0.5rem",
  xl: "0.75rem",
  "2xl": "1rem",
  "3xl": "1.5rem",
  full: "9999px"
};
var transitions = {
  duration: {
    fastest: "50ms",
    faster: "100ms",
    fast: "150ms",
    normal: "200ms",
    slow: "300ms",
    slower: "400ms",
    slowest: "500ms"
  },
  timing: {
    linear: "linear",
    ease: "ease",
    easeIn: "ease-in",
    easeOut: "ease-out",
    easeInOut: "ease-in-out"
  }
};
var zIndex = {
  hide: -1,
  base: 0,
  docked: 10,
  dropdown: 1e3,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  toast: 1600,
  tooltip: 1700
};

// src/tokens/semantic.ts
var defaultSemanticTokens = {
  colors: {
    background: {
      base: "var(--pc-bg-base)",
      subtle: "var(--pc-bg-subtle)",
      muted: "var(--pc-bg-muted)",
      emphasized: "var(--pc-bg-emphasized)",
      inverted: "var(--pc-bg-inverted)"
    },
    foreground: {
      base: "var(--pc-fg-base)",
      muted: "var(--pc-fg-muted)",
      subtle: "var(--pc-fg-subtle)",
      inverted: "var(--pc-fg-inverted)"
    },
    primary: {
      base: "var(--pc-primary)",
      hover: "var(--pc-primary-hover)",
      active: "var(--pc-primary-active)",
      subtle: "var(--pc-primary-subtle)",
      muted: "var(--pc-primary-muted)",
      foreground: "var(--pc-primary-fg)"
    },
    secondary: {
      base: "var(--pc-secondary)",
      hover: "var(--pc-secondary-hover)",
      active: "var(--pc-secondary-active)",
      subtle: "var(--pc-secondary-subtle)",
      foreground: "var(--pc-secondary-fg)"
    },
    border: {
      base: "var(--pc-border)",
      muted: "var(--pc-border-muted)",
      subtle: "var(--pc-border-subtle)",
      emphasized: "var(--pc-border-emphasized)",
      focus: "var(--pc-border-focus)"
    },
    success: {
      base: "var(--pc-success)",
      hover: "var(--pc-success-hover)",
      subtle: "var(--pc-success-subtle)",
      foreground: "var(--pc-success-fg)"
    },
    warning: {
      base: "var(--pc-warning)",
      hover: "var(--pc-warning-hover)",
      subtle: "var(--pc-warning-subtle)",
      foreground: "var(--pc-warning-fg)"
    },
    error: {
      base: "var(--pc-error)",
      hover: "var(--pc-error-hover)",
      subtle: "var(--pc-error-subtle)",
      foreground: "var(--pc-error-fg)"
    },
    info: {
      base: "var(--pc-info)",
      hover: "var(--pc-info-hover)",
      subtle: "var(--pc-info-subtle)",
      foreground: "var(--pc-info-fg)"
    },
    input: {
      background: "var(--pc-input-bg)",
      border: "var(--pc-input-border)",
      borderFocus: "var(--pc-input-border-focus)",
      borderError: "var(--pc-input-border-error)",
      placeholder: "var(--pc-input-placeholder)"
    },
    overlay: {
      base: "var(--pc-overlay)",
      heavy: "var(--pc-overlay-heavy)"
    }
  },
  spacing: {
    page: {
      x: "var(--pc-space-page-x)",
      y: "var(--pc-space-page-y)"
    },
    section: {
      gap: "var(--pc-space-section-gap)"
    },
    card: {
      padding: "var(--pc-space-card-padding)",
      gap: "var(--pc-space-card-gap)"
    },
    input: {
      x: "var(--pc-space-input-x)",
      y: "var(--pc-space-input-y)"
    },
    button: {
      x: "var(--pc-space-btn-x)",
      y: "var(--pc-space-btn-y)",
      gap: "var(--pc-space-btn-gap)"
    }
  },
  typography: {
    heading: {
      h1: {
        size: "var(--pc-text-h1)",
        weight: "var(--pc-weight-h1)",
        lineHeight: "var(--pc-lh-h1)"
      },
      h2: {
        size: "var(--pc-text-h2)",
        weight: "var(--pc-weight-h2)",
        lineHeight: "var(--pc-lh-h2)"
      },
      h3: {
        size: "var(--pc-text-h3)",
        weight: "var(--pc-weight-h3)",
        lineHeight: "var(--pc-lh-h3)"
      },
      h4: {
        size: "var(--pc-text-h4)",
        weight: "var(--pc-weight-h4)",
        lineHeight: "var(--pc-lh-h4)"
      }
    },
    body: {
      lg: {
        size: "var(--pc-text-lg)",
        weight: "var(--pc-weight-normal)",
        lineHeight: "var(--pc-lh-lg)"
      },
      base: {
        size: "var(--pc-text-base)",
        weight: "var(--pc-weight-normal)",
        lineHeight: "var(--pc-lh-base)"
      },
      sm: {
        size: "var(--pc-text-sm)",
        weight: "var(--pc-weight-normal)",
        lineHeight: "var(--pc-lh-sm)"
      },
      xs: {
        size: "var(--pc-text-xs)",
        weight: "var(--pc-weight-normal)",
        lineHeight: "var(--pc-lh-xs)"
      }
    },
    label: {
      size: "var(--pc-text-sm)",
      weight: "var(--pc-weight-medium)",
      lineHeight: "var(--pc-lh-sm)"
    },
    caption: {
      size: "var(--pc-text-xs)",
      weight: "var(--pc-weight-normal)",
      lineHeight: "var(--pc-lh-xs)"
    }
  },
  shadows: {
    card: "var(--pc-shadow-card)",
    dropdown: "var(--pc-shadow-dropdown)",
    modal: "var(--pc-shadow-modal)",
    popover: "var(--pc-shadow-popover)",
    toast: "var(--pc-shadow-toast)"
  },
  radius: {
    button: "var(--pc-radius-btn)",
    input: "var(--pc-radius-input)",
    card: "var(--pc-radius-card)",
    modal: "var(--pc-radius-modal)",
    badge: "var(--pc-radius-badge)",
    full: "var(--pc-radius-full)"
  }
};

// src/index.ts
var UI_VERSION = "0.0.1";
export {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  FontLoader,
  Form,
  FormDescription,
  FormError,
  FormField,
  FormLabel,
  Input,
  Select,
  Switch,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  ThemeContext,
  ThemeProvider,
  ToastProvider,
  Tooltip,
  UI_VERSION,
  arc2Theme,
  atlasDisplayFont,
  atlasFontLink,
  atlasTheme,
  baseRadius,
  baseShadows,
  baseSpacing,
  baseTransitions,
  baseTypography,
  brandchordTheme,
  carbonFontLink,
  carbonTheme,
  cn,
  colors,
  createProductTheme,
  defaultSemanticTokens,
  defaultTheme,
  designSystemThemes,
  getFontConfig,
  getFontLinkTags,
  mazoTheme,
  memoriaTheme,
  meridianDisplayFont,
  meridianFontLink,
  meridianTheme,
  productPalettes,
  productThemes,
  radii,
  shadows,
  signalFontLinks,
  signalTheme,
  spacing,
  themes,
  transitions,
  typography,
  useTheme,
  useToast,
  zIndex
};
